export enum MediaType {
  brightcove= 'BRIGHTCOVE',
  brightcoveLive= 'BRIGHTCOVE_LIVE',
  ooyala= 'OOYALA',
  kaltura= 'KALTURA',
  flv= 'FLV',
  audio= 'AUDIO',
  cloudfront= 'CLOUDFRONT',
  recording= 'RECORDING',
  existingRecording= 'EXISTING_RECORDING',
  livestream= 'LIVESTREAM',
  mediaLive= 'MEDIA_LIVE',
  ustream= 'USTREAM',
  remote= 'REMOTE',
  noSource= 'NO_SOURCE',
  combo= 'COMBO',
  sequenceMedia= 'SEQUENCE_MEDIA',
}

export enum CuePointType {
  animation = 'ANIMATION',
  assessment = 'ASSESSMENT',
  blank = 'BLANK',
  form = 'FORM',
  html = 'HTML',
  iframe = 'IFRAME',
  image = 'IMAGE',
  ispring = 'ISPRING',
  swf = 'SWF',
  zoom = 'ZOOM',
  noSource = 'NO_SOURCE',
}

export enum AttachmentType {
  pdf = 'PDF',
  doc = 'DOC',
  ppt = 'PPT',
  xls = 'XLS',
  csv = 'CSV',
  xml = 'XML',
  rtf = 'RTF',
  txt = 'TXT',
  jpeg = 'JPEG',
  gif = 'GIF',
  png = 'PNG',
  html = 'HTML',
  mp3 = 'MP3',
  mp4 = 'MP4',
  mov = 'MOV',
  wmv = 'WMV',
  avi = 'AVI',
  flv = 'FLV',
  swf = 'SWF',
  exe = 'EXE',
  zip = 'ZIP',
  unknown = 'UNKNOWN',
  url = 'URL',
}

export enum TemplateType {
  embed= 'EMBED',
  default= 'DEFAULT',
  custom= 'CUSTOM',
  mobile= 'MOBILE',
}

export enum TemplateFormat {
  xml= 'XML',
  html= 'HTML',
}

export interface Presentation {
  project: Project;
  flyout?: Flyout;
  // The Next-Gen Player wraps the whole payload under `presentation`; legacy exposes `project` at the root.
  presentation?: Presentation;
}
export interface Project {
  metadata: ProjectMetadata;
  timeline: Timeline;
  staticContent: StaticContent;
  templates: TemplateList;
  sourceContent: SourceContent;
  flyout?: Flyout;
}
export interface Flyout {
  isEnabled: boolean;
  primaryColor: string;
  customCssUrl: string;
  standbyScreenSettings: unknown | null;
  tabs: FlyoutTabs;
}
export interface FlyoutTabs {
  overviewTab?: FlyoutTab;
  questionAndAnswerTab?: FlyoutTab;
  pollsTab?: FlyoutTab;
  speakersTab?: FlyoutTab;
  chaptersTab?: FlyoutTab;
  liveQATab?: FlyoutTab;
  customTabs?: FlyoutTab[];
}
export interface FlyoutTab {
  label: string;
  hidden: boolean;
  selected: boolean;
  order: number;
}
export interface ProjectMetadata {
  title: string;
  displayName: string;
  shortDescription: string;
  longDescription: string;
  thumbnail: string;
  posterImage: string;
  organization: string;
  shortcut: string;
  domainRestrictions: DomainRestrictionList;
  stats: Stats;
  lastModifiedBy: HistoryRecord;
}
export interface DomainRestrictionList {
  domainRestriction?: DomainRestriction[];
}
export interface DomainRestriction {
  domain: string;
}
export interface Stats {
  duration: number;
  sequences: number;
  segments: number;
  hiddenSegments?: number;
  cuePoints: number;
  assessments?: number;
  attachmentLists: number;
  attachments: number;
  slideLists: number;
  slides: number;
  templates: number;
}
export interface HistoryRecord {
  appName: string;
  appVersion: string;
}
export interface Timeline {
  playbackSettings: PlaybackSettings;
  media?: Media;
  sequences: SequenceList;
}
export interface PlaybackSettings {
  autoStart: boolean;
  autoResume: boolean;
  allowNavigationForward: boolean;
  allowNavigationBackward: boolean;
  pauseOnLinkClick?: boolean;
  autoLoop?: boolean;
  autoPauseSlideChanges?: boolean;
  enableSlideControls?: boolean;
  enablePlaybackRateControls?: boolean;
  redirect?: Redirect;
  enableDvrNavigation?: boolean;
}
export interface Redirect {
  enabled: boolean;
  url: string;
}
export interface Media {
  type: MediaType;
  sources: MediaSourceList;
  inPoint: number;
  outPoint: number;
  duration: number;
}
export interface MediaSourceList {
  source?: MediaSource[];
}
export interface MediaSource {
  type: string;
  fileURL: string;
  properties: MediaSourcePropertyList;
}
export interface MediaSourcePropertyList {
  property?: MediaSourceProperty[];
}
export interface MediaSourceProperty {
  name: string;
  value: string;
}
export interface SequenceList {
  sequence: Sequence[];
}
export interface Sequence {
  metadata: BasicMetadata;
  segments: SegmentList;
  media?: Media;
  liveBroadcast?: LiveBroadcast;
  layout?: SequenceLayout;
  captions?: CaptionsList;
  transcript?: Transcript;
  autoTranscript?: Transcript;
}
export interface BasicMetadata {
  displayName: string;
  shortDescription: string;
  longDescription: string;
}
export interface SegmentList {
  segment: Segment[];
}
export interface Segment {
  metadata: BasicMetadata;
  live: Live;
  transcript: Transcript;
  media: Media;
  tracks: TrackList;
  hidden?: boolean;
}
export interface Live {
  enabled: boolean;
  latency: number;
  refreshURL: string;
}
export interface Transcript {
  source: string;
  text: string;
  assetId: number;
}
export interface TrackList {
  track: Track[];
}
export interface Track {
  settings: TrackSettings;
  cuePoints: CuePointList;
}
export interface TrackSettings {
  offsetEnabled: boolean;
  offsetTime: number;
}
export interface CuePointList {
  cuePoint?: CuePoint[];
}
export interface CuePoint {
  time: number;
  type: CuePointType;
  source: CuePointSource;
  settings: CuePointSettings;
  metadata: CuePointMetadata;
}
export interface CuePointSource {
  url: string;
  image: string;
  properties: CuePointSourcePropertyList;
}
export interface CuePointSourcePropertyList {
  property?: CuePointSourceProperty[];
}
export interface CuePointSourceProperty {
  name: string;
  value: string;
}
export interface CuePointSettings {
  pause: boolean;
  zoom: number;
  key: boolean;
  javascript?: string;
}
export interface CuePointMetadata {
  title: string;
  footnote?: string;
  smallThumbnail: string;
  largeThumbnail: string;
  speakerNotes?: string;
}
export interface LiveBroadcast {
  commandFileURL: string;
  status: string;
  version: number;
}
export interface SequenceLayout {
  videoAspectRatio: AspectRatio;
  slideAspectRatio: AspectRatio;
  invert: boolean;
}
export interface AspectRatio {
  width: number;
  height: number;
}
export interface CaptionsList {
  caption?: Caption[];
}
export interface Caption {
  languageLabel: string;
  languageCode: string;
  fileURL: string;
  properties: CaptionPropertyList;
}
export interface CaptionPropertyList {
  property?: CaptionProperty[];
}
export interface CaptionProperty {
  name: string;
  value: string;
}
export interface StaticContent {
  attachmentLists: AttachmentListObjectList;
}
export interface AttachmentListObjectList {
  attachmentList?: AttachmentListObject[];
}
export interface AttachmentListObject {
  attachments: AttachmentList;
}
export interface AttachmentList {
  attachment?: Attachment[];
}
export interface Attachment {
  type: AttachmentType;
  displayName: string;
  description: string;
  url: string;
  link: boolean;
  size: number;
  thumbnailUrl?: string;
  properties?: AttachmentPropertyList;
}
export interface AttachmentPropertyList {
  property?: AttachmentProperty[];
}
export interface AttachmentProperty {
  name: string;
  value: string;
}
export interface TemplateList {
  template: Template[];
}
export interface Template {
  name: string;
  type: TemplateType;
  width: string;
  height: string;
  backgroundColor?: string;
  properties: TemplatePropertyList;
  layout: string;
  source: string;
  format?: TemplateFormat;
}
export interface TemplatePropertyList {
  property?: TemplateProperty[];
}
export interface TemplateProperty {
  id: string;
  tag: string;
  name: string;
  value: string;
  original: string;
}
export interface SourceContent {
  slideLists: SlideListObjectList;
  mediaList?: MediaList;
}
export interface SlideListObjectList {
  slideList: SlideListObject[];
}
export interface SlideListObject {
  cuePoints: CuePointList;
}
export interface MediaList {
  media?: Media[];
}
export type SourceWithoutProperties = Pick<CuePointSource, Exclude<keyof CuePointSource, 'properties'>>;
